import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  DATE_TIME_FORMAT,
  PER_PAGE_DEFAULT,
  QUERY_NAME,
  ROUTES,
  SERVICES,
} from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import axios from "@lhd/libs/axios";
import { normalizeJapaneseDateInput } from "@lhd/features/TrackingTime/ListTracking/monthlyListQuery";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";
import { broadcastWorkerLogtimePunch } from "@lhd/features/TrackingTime/workerLogtimePunchSync";
import { getCompanyId } from "@lhd/utility";
import {
  CompaniesWithLogtimeItem,
  CurrentTimeInfo,
  LogTimeByDateResponse,
  WorkerLogtimeData,
  WorkerLogtimeMonthlyRecord,
  WorkerLogtimePermissionResponse,
  WorkerLogtimeResponse,
  User,
} from "@lhd/types";
import { useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const INACTIVE_LOGTIME_STATUS_KEYS = new Set(["checked_out", "not_checked_in"]);

const normalizeCompaniesWithLogtimePayload = (
  raw: unknown,
): CompaniesWithLogtimeItem[] => {
  if (Array.isArray(raw)) return raw as CompaniesWithLogtimeItem[];
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.companies)) {
      return o.companies as CompaniesWithLogtimeItem[];
    }
    if (Array.isArray(o.data)) {
      return o.data as CompaniesWithLogtimeItem[];
    }
  }
  return [];
};

export const parseActiveCompanyIdFromCompaniesWithLogtime = (
  raw: unknown,
): string | null => {
  const rows = normalizeCompaniesWithLogtimePayload(raw);
  if (!rows.length) return null;

  for (const row of rows) {
    const id = row.company_id ?? row.id;
    if (id == null) continue;

    if (row.has_logtime === false) continue;

    const statusKey =
      row.logtime_status != null ? String(row.logtime_status) : null;
    if (statusKey && INACTIVE_LOGTIME_STATUS_KEYS.has(statusKey)) continue;

    return String(id);
  }

  const first = rows[0];
  const firstId = first?.company_id ?? first?.id;
  return firstId != null ? String(firstId) : null;
};

const serializeCompanyIdsParams = (companyIds: number[]) => {
  const sp = new URLSearchParams();
  companyIds.forEach((id) => {
    sp.append("company_ids[]", String(id));
  });
  return sp.toString();
};

export const fetchCompaniesWithLogtime = async (companyIds: number[]) => {
  const res = await axios({
    method: "get",
    url: `${SERVICES.WORKER_LOGTIMES}/companies_with_logtime`,
    params: { company_ids: companyIds },
    paramsSerializer: {
      serialize: (params) =>
        serializeCompanyIdsParams(
          (params as { company_ids: number[] }).company_ids ?? [],
        ),
    },
  });

  return res.data;
};

export const useFetchCompaniesWithLogtime = (companyIds: number[]) => {
  const sortedIds = [...companyIds].sort((a, b) => a - b);

  return useQuery(
    [QUERY_NAME.WORKER_LOGTIMES, "companies_with_logtime", sortedIds],
    () => fetchCompaniesWithLogtime(sortedIds),
    {
      enabled: sortedIds.length > 0,
      staleTime: 300,
      cacheTime: 300,
    },
  );
};

export const resolveCurrentWorkerIdByCompany = (
  auth: User | null,
  companyId: string,
): number | undefined => {
  const wid = auth?.workers?.find(
    (w) => w?.company_id?.toString() === companyId?.toString(),
  )?.id;
  if (wid == null) return undefined;
  const parsed = Number(wid);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const resolvePositiveWorkerId = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const useFetchWorkerLogTimes = () => {
  const companyId = getCompanyId();
  const auth = useSelector(user);
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, companyId);
  return useQuery(
    [QUERY_NAME.WORKER_LOGTIMES, companyId, currentWorkerId ?? null],
    () => {
      if (currentWorkerId == null) {
        throw new Error("worker_id is required for worker_logtimes status API");
      }
      return axios({
        method: "get",
        url: SERVICES.WORKER_LOGTIMES + "/status",
        params: {
          company_id: companyId,
          worker_id: currentWorkerId,
        },
      }).then((res) => ({
        data: res.data as WorkerLogtimeData,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      cacheTime: 300,
      staleTime: 300,
      enabled: !!companyId && currentWorkerId != null,
    },
  );
};

export type PostWorkerLogtimeCreatePayload = {
  company_id: string;
  worker_id: number;
  work_date: string;
  log_type: string;
  logged_at?: string | null;
  edit_logtime_at?: boolean;
  latitude?: string | number | null;
  longitude?: string | number | null;
  note?: string | null;
  files?: File[];
};

export const postWorkerLogtimeCreate = async (
  payload: PostWorkerLogtimeCreatePayload,
) => {
  const form = new FormData();
  form.append("company_id", String(payload.company_id));
  form.append("worker_id", String(payload.worker_id));
  form.append("work_date", payload.work_date);
  form.append("log_type", payload.log_type);
  if (typeof payload.edit_logtime_at === "boolean") {
    form.append("edit_logtime_at", String(payload.edit_logtime_at));
  }
  if (payload.logged_at == null || String(payload.logged_at).trim() === "") {
    form.append("logged_at", "");
  } else {
    form.append("logged_at", String(payload.logged_at));
  }
  if (payload.latitude != null && String(payload.latitude).trim() !== "") {
    form.append("latitude", String(payload.latitude));
  }
  if (payload.longitude != null && String(payload.longitude).trim() !== "") {
    form.append("longitude", String(payload.longitude));
  }
  if (payload.note != null && String(payload.note).trim() !== "") {
    form.append("note", String(payload.note));
  }
  if (payload.files?.length) {
    payload.files.forEach((file: File) => {
      form.append("files[]", file);
    });
  }
  return axios.post(SERVICES.WORKER_LOGTIMES, form);
};

const getAxiosErrorResponseData = (error: unknown): unknown => {
  if (error == null || typeof error !== "object" || !("response" in error)) {
    return undefined;
  }
  const response = (error as { response?: { data?: unknown } }).response;
  return response?.data;
};

export const getWorkerLogtimeApiErrorMessage = (
  error: unknown,
  fallback = "打刻に失敗しました。",
): string => {
  const data = getAxiosErrorResponseData(error);
  if (data != null && typeof data === "object") {
    const body = data as Record<string, unknown>;

    if (Array.isArray(body.errors)) {
      const first = body.errors[0];
      if (typeof first === "string" && first.trim()) return first.trim();
      if (first != null && typeof first === "object") {
        const nested = (first as { message?: unknown; msg?: unknown }).message;
        if (typeof nested === "string" && nested.trim()) return nested.trim();
        const nestedMsg = (first as { msg?: unknown }).msg;
        if (typeof nestedMsg === "string" && nestedMsg.trim()) {
          return nestedMsg.trim();
        }
      }
    }

    if (typeof body.msg === "string" && body.msg.trim()) {
      return body.msg.trim();
    }
    if (typeof body.message === "string" && body.message.trim()) {
      return body.message.trim();
    }
    if (typeof body.error === "string" && body.error.trim()) {
      return body.error.trim();
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
};

export const parseWorkerLogtimeCreateResponseId = (res: {
  data?: unknown;
}): number | undefined => {
  const idFromObject = (o: Record<string, unknown>): number | undefined => {
    const raw = o.id;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === "string") {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return undefined;
  };

  const root = res?.data;
  if (root == null) return undefined;
  if (typeof root === "number" || typeof root === "string") {
    const n = Number(root);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  if (typeof root === "object") {
    const o = root as Record<string, unknown>;
    const direct = idFromObject(o);
    if (direct != null) return direct;
    for (const k of ["worker_logtime", "data", "logtime"]) {
      const inner = o[k];
      if (inner && typeof inner === "object") {
        const nested = idFromObject(inner as Record<string, unknown>);
        if (nested != null) return nested;
      }
    }
  }
  return undefined;
};

export const useSubmitWorkerLogTimes = () => {
  const queryClient = useQueryClient();
  const company_id = getCompanyId();
  const auth = useSelector(user);
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, company_id);

  return useMutation(
    async ({ payload }: any) => {
      const form = new FormData();
      form.append("company_id", String(company_id));
      const payloadWorkerId = resolvePositiveWorkerId(payload?.worker_id);
      const resolvedWorkerId = payloadWorkerId ?? currentWorkerId;
      if (resolvedWorkerId == null) {
        throw new Error("worker_id is required for worker_logtimes API");
      }
      form.append("worker_id", String(resolvedWorkerId));

      // If the payload has additional fields → append all of them
      Object.keys(payload || {}).forEach((key) => {
        if (key !== "files" && key !== "worker_id") {
          form.append(key, payload[key]);
        }
      });

      // Append file array
      if (payload?.files?.length) {
        payload.files.forEach((file: File) => {
          form.append("files[]", file);
        });
      }

      return axios.post(SERVICES.WORKER_LOGTIMES, form);
    },
    {
      onSuccess(_res, variables) {
        const company_id = getCompanyId();
        queryClient.invalidateQueries([QUERY_NAME.WORKER_LOGTIMES, company_id]);

        const payload = variables?.payload as
          | PostWorkerLogtimeCreatePayload
          | undefined;
        const payloadWorkerId = resolvePositiveWorkerId(payload?.worker_id);
        const resolvedWorkerId = payloadWorkerId ?? currentWorkerId;
        const logType = payload?.log_type as LOG_TYPE | undefined;

        if (
          company_id &&
          resolvedWorkerId != null &&
          logType &&
          Object.values(LOG_TYPE).includes(logType)
        ) {
          const workDate =
            String(payload?.work_date ?? "").trim() ||
            moment().format("YYYY-MM-DD");
          broadcastWorkerLogtimePunch({
            companyId: company_id,
            workerId: resolvedWorkerId,
            workDate,
            logType,
          });
        }
      },
    },
  );
};

export const useFetchCurrentTime = () => {
  const companyId = getCompanyId();
  return useQuery({
    queryKey: ["currentTime", companyId],
    queryFn: async () => {
      const tStart = new Date();
      const data = (
        await axios({
          method: "get",
          url: SERVICES.WORKER_LOGTIMES + "/current_datetime",
          params: { company_id: companyId },
        })
      ).data as CurrentTimeInfo;
      const tEnd = new Date();
      const timeDif = tEnd.getTime() - tStart.getTime();
      const tBEnow = new Date(data.current_datetime).getTime();
      return {
        data: data,
        datetimeWithTimeRes: new Date(tBEnow + timeDif / 2),
      };
    },
    enabled: !!companyId,
    cacheTime: 300,
    staleTime: 300,
  });
};

//get /api/v1/worker_logtimes/companies
export const useFetchWorkerLogTimesCompanies = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.WORKER_LOGTIMES, QUERY_NAME.COMPANIES, companyId],
    async () => {
      return await axios({
        method: "get",
        url: SERVICES.WORKER_LOGTIMES + "/companies",
        params: { company_id: companyId },
      });
    },
    {
      cacheTime: 300,
      staleTime: 300,
    },
  );
};

const JP_FILTER_DATE_FORMATS = [
  DATE_TIME_FORMAT.DATE_JP,
  DATE_TIME_FORMAT.DATE_JP_MD,
  DATE_TIME_FORMAT.DATE_MONTH_JP,
  DATE_TIME_FORMAT.DATE_MONTH_JP_M,
  DATE_TIME_FORMAT.YEAR_JP,
  "YYYY-MM-DD",
  "YYYY-MM",
  "YYYY",
];

const toApiDateParam = (date?: string) => {
  if (!date?.trim()) return undefined;
  const normalized = normalizeJapaneseDateInput(date);

  const yearOnly = moment(normalized, [DATE_TIME_FORMAT.YEAR_JP, "YYYY"], true);
  if (yearOnly.isValid()) {
    return yearOnly.format("YYYY");
  }

  const monthOnly = moment(
    normalized,
    [
      "YYYY-MM",
      DATE_TIME_FORMAT.DATE_MONTH_JP,
      DATE_TIME_FORMAT.DATE_MONTH_JP_M,
    ],
    true,
  );
  if (monthOnly.isValid()) {
    return monthOnly.format("YYYY-MM");
  }

  const m = moment(normalized, JP_FILTER_DATE_FORMATS, true);
  if (m.isValid()) {
    return m.format("YYYY-MM-DD");
  }
  return normalized.replace(/\//g, "-");
};

const splitDateInCsv = (raw: string): string[] =>
  raw
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);

const formatOneDateInValueToIso = (value: string): string => {
  const normalized = normalizeJapaneseDateInput(value);
  const withTimeFormats = [
    DATE_TIME_FORMAT.DATE_TIME_JP,
    DATE_TIME_FORMAT.DATE_JP_TIME,
    DATE_TIME_FORMAT.DATE_TIME_JP_MD,
    DATE_TIME_FORMAT.DATE_JP_MD_TIME,
  ];
  for (const fmt of withTimeFormats) {
    const m = moment(normalized, fmt, true);
    if (m.isValid()) {
      return `${m.format("YYYY-MM-DD")}T${m.format("HH:mm:ss")}+09:00`;
    }
  }
  const dateOnlyFormats = [
    DATE_TIME_FORMAT.DATE_JP,
    DATE_TIME_FORMAT.DATE_JP_MD,
  ];
  for (const fmt of dateOnlyFormats) {
    const m = moment(normalized, fmt, true);
    if (m.isValid()) {
      return `${m.format("YYYY-MM-DD")}T00:00:00+09:00`;
    }
  }
  const iso = moment.parseZone(normalized);
  if (iso.isValid()) return iso.format("YYYY-MM-DDTHH:mm:ssZ");
  return normalized;
};

const toApiDateInParam = (raw?: string) => {
  if (!raw?.trim()) return undefined;
  const values = splitDateInCsv(raw).map(formatOneDateInValueToIso);
  return values.length ? values.join(",") : undefined;
};

const isWorkerLogtimeAccessDeniedStatus = (status?: number) =>
  status === 403 || status === 404;

const workerLogtimeQueryRetry = (failureCount: number, error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (isWorkerLogtimeAccessDeniedStatus(status)) return false;
  return failureCount < 3;
};

const resolveMonthlyWorkerId = (
  isAdmin: boolean,
  paramsWorkerId: number | undefined,
  currentWorkerId: number | undefined,
) => {
  if (paramsWorkerId != null) return paramsWorkerId;
  if (!isAdmin) return currentWorkerId;
  return undefined;
};

export const useFetchWorkerLogTimesByDate = (
  date?: string,
  workerId?: number,
  options?: { enabled?: boolean },
) => {
  const companyId = getCompanyId();
  const dateParam = toApiDateParam(date);
  const navigate = useNavigate();
  const queryEnabled = options?.enabled ?? true;

  return useQuery(
    [
      QUERY_NAME.WORKER_LOGTIMES,
      companyId,
      "by_date",
      dateParam,
      workerId ?? null,
    ],
    () =>
      fetchWorkerLogTimesByDate({
        companyId: String(companyId),
        date: String(dateParam),
        workerId,
      }),
    {
      cacheTime: 300,
      staleTime: 300,
      enabled: queryEnabled && !!dateParam && !!companyId,
      retry: workerLogtimeQueryRetry,
      onError(error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (!isWorkerLogtimeAccessDeniedStatus(status)) return;
        navigate(`/${ROUTES.NOT_FOUND_PAGE}`, { replace: true });
      },
    },
  );
};

export const fetchWorkerLogTimesByDate = async (params: {
  companyId: string;
  date: string;
  workerId?: number;
}): Promise<LogTimeByDateResponse> => {
  const response = await axios({
    method: "get",
    url: SERVICES.WORKER_LOGTIMES + "/by_date",
    params: {
      company_id: params.companyId,
      date: params.date,
      ...(typeof params.workerId === "number" &&
      Number.isFinite(params.workerId) &&
      params.workerId > 0
        ? { worker_id: params.workerId }
        : {}),
    },
  });

  return response.data as LogTimeByDateResponse;
};

const normalizeLogtimePermissionWorkerIds = (ids: unknown): number[] => {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

export const hasWorkerLogtimeViewPermission = (
  permissions: WorkerLogtimePermissionResponse | undefined,
  workerId: number | undefined,
): boolean => {
  if (!permissions || workerId == null) return false;
  return normalizeLogtimePermissionWorkerIds(permissions.logtime_view).includes(
    workerId,
  );
};

export const hasWorkerLogtimeEditPermission = (
  permissions: WorkerLogtimePermissionResponse | undefined,
  workerId: number | undefined,
): boolean => {
  if (!permissions || workerId == null) return false;
  return normalizeLogtimePermissionWorkerIds(permissions.logtime_edit).includes(
    workerId,
  );
};

export const useFetchWorkerLogtimePermissions = (enabled = true) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();

  return useQuery(
    [QUERY_NAME.LOGTIME_PERMISSIONS, "worker_logtimes", companyId],
    async () => {
      const res = await axios({
        method: "get",
        url: `${SERVICES.WORKER_LOGTIMES}/logtime_permissions`,
        params: {
          company_id: companyId,
        },
      });
      const data = res.data as WorkerLogtimePermissionResponse;
      return {
        logtime_view: normalizeLogtimePermissionWorkerIds(data?.logtime_view),
        logtime_edit: normalizeLogtimePermissionWorkerIds(data?.logtime_edit),
        logtime_create_delete: normalizeLogtimePermissionWorkerIds(
          data?.logtime_create_delete,
        ),
      };
    },
    {
      enabled: !!companyId && enabled,
      cacheTime: 300,
      staleTime: 300,
      retry: workerLogtimeQueryRetry,
      onError(error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (!isWorkerLogtimeAccessDeniedStatus(status)) return;
        navigate(`/${ROUTES.NOT_FOUND_PAGE}`, { replace: true });
      },
    },
  );
};

function normalizeMonthlyPayload(raw: unknown): WorkerLogtimeMonthlyRecord[] {
  if (Array.isArray(raw)) {
    return raw as WorkerLogtimeMonthlyRecord[];
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const k of [
      "data",
      "worker_logtimes",
      "logtimes",
      "records",
    ] as const) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v as WorkerLogtimeMonthlyRecord[];
      }
    }
  }
  return [];
}

const toPositiveIntOr = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export type WorkerLogtimeMonthlyMeta = {
  currentPage: number;
  perPage: number;
  totalRecords: number;
  totalPages: number;
};

export type WorkerLogtimeMonthlyResult = {
  records: WorkerLogtimeMonthlyRecord[];
  meta: WorkerLogtimeMonthlyMeta;
};

const normalizeMonthlyMeta = (
  raw: unknown,
  fallbackPage: number,
  fallbackPerPage: number,
): WorkerLogtimeMonthlyMeta => {
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const pagination =
      o.pagination && typeof o.pagination === "object"
        ? (o.pagination as Record<string, unknown>)
        : null;

    const source = pagination ?? o;
    const currentPage = toPositiveIntOr(
      source.current_page ?? source.currentPage,
      fallbackPage,
    );
    const perPage = toPositiveIntOr(
      source.per_page ?? source.perPage,
      fallbackPerPage,
    );
    const totalRecords = toPositiveIntOr(
      source.total_records ?? source.totalRecords,
      0,
    );
    const totalPages = toPositiveIntOr(
      source.total_pages ?? source.totalPages,
      Math.ceil(totalRecords / Math.max(perPage, 1)) || 1,
    );

    return {
      currentPage,
      perPage,
      totalRecords,
      totalPages,
    };
  }

  return {
    currentPage: fallbackPage,
    perPage: fallbackPerPage,
    totalRecords: 0,
    totalPages: 1,
  };
};

export type WorkerLogtimesMonthlyQParams = {
  workerNameCont?: string;
  workerNameContAny?: string;
  companyNameAny?: string;
  noteCont?: string;
  noteContAny?: string;
  logtimeStatusIn?: string;
  creatorNameCont?: string;
  creatorNameContAny?: string;
  firstCheckinTimeGteq?: string;
  firstCheckinTimeLteq?: string;
  lastCheckoutTimeGteq?: string;
  lastCheckoutTimeLteq?: string;
  createdAtGteq?: string;
  createdAtLteq?: string;
  filterCreatedAt?: string;
  filterFirstCheckinTime?: string;
  filterLastCheckoutTime?: string;
  dateIn?: string;
};

export type WorkerLogtimesMonthlyParams = {
  year: string;
  month: string;
  workerId?: number;
  page?: number;
  perPage?: number;
  filterLogtimeStatuses?: string;
  sort?: Record<string, "asc" | "desc">;
  q?: WorkerLogtimesMonthlyQParams;
  filters?: Record<string, string[]>;
  isWithoutWorker?: boolean;
};

export type WorkerLogtimesFilterOptionsParams = {
  year: string;
  month: string;
  workerId?: number;
  refreshKey?: string;
  activeFilters?: Record<string, string[]>;
};

export type WorkerLogtimesFilterOptionsResponse = {
  dates?: string[];
  company_names?: string[];
  worker_names?: string[];
  notes?: string[];
  logtime_statuses?: { value: string; label: string }[];
  creator_names?: string[];
  created_at?: { values?: string[] };
  first_checkin_time?: { values?: string[] };
  last_checkout_time?: { values?: string[] };
};

const buildMonthlyQAxiosParams = (
  q?: WorkerLogtimesMonthlyQParams,
): Record<string, string> => {
  if (!q) return {};
  const out: Record<string, string> = {};
  const set = (key: string, val?: string) => {
    const t = val?.trim();
    if (t) out[`q[:${key}]`] = t;
  };
  set("logtime_status_in", q.logtimeStatusIn);
  set("first_checkin_time_gteq", toApiDateParam(q.firstCheckinTimeGteq));
  set("first_checkin_time_lteq", toApiDateParam(q.firstCheckinTimeLteq));
  set("last_checkout_time_gteq", toApiDateParam(q.lastCheckoutTimeGteq));
  set("last_checkout_time_lteq", toApiDateParam(q.lastCheckoutTimeLteq));
  set("created_at_gteq", toApiDateParam(q.createdAtGteq));
  set("created_at_lteq", toApiDateParam(q.createdAtLteq));
  set("date_in", toApiDateInParam(q.dateIn));
  return out;
};

const buildMonthlyFiltersAxiosParams = (
  filters?: Record<string, string[]>,
): Record<string, string[]> => {
  if (!filters) return {};
  return Object.entries(filters).reduce((acc, [key, values]) => {
    const cleaned = (values || [])
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    if (cleaned.length) {
      acc[`filters[${key}][]`] = cleaned;
    }
    return acc;
  }, {} as Record<string, string[]>);
};

export const useFetchWorkerLogtimesMonthly = (
  params: WorkerLogtimesMonthlyParams | null,
) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const auth = useSelector(user);
  const isAdmin = !!auth?.is_admin;
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, companyId);
  const year = params?.year?.trim();
  const month = params?.month?.trim();
  const isWithoutWorker = !!params?.isWithoutWorker;

  const workerId = isWithoutWorker
    ? null
    : resolveMonthlyWorkerId(isAdmin, params?.workerId, currentWorkerId);
  const page = toPositiveIntOr(params?.page, 1);
  const perPage = toPositiveIntOr(params?.perPage, 50);
  const filterLogtimeStatuses = params?.filterLogtimeStatuses?.trim();
  const sortParams: Record<string, string> = {};
  if (params?.sort) {
    for (const [field, dir] of Object.entries(params.sort)) {
      const f = field?.trim();
      if (!f || (dir !== "asc" && dir !== "desc")) continue;
      sortParams[`sort[${f}]`] = dir;
    }
  }
  const sortCacheKey =
    Object.keys(sortParams).length > 0
      ? Object.keys(sortParams)
          .sort()
          .map((k) => `${k}=${sortParams[k]}`)
          .join("&")
      : "";
  const qParams = buildMonthlyQAxiosParams(params?.q);
  const filterCreatedAtParam = toApiDateParam(params?.q?.filterCreatedAt);
  const filterFirstCheckinTimeParam = toApiDateParam(
    params?.q?.filterFirstCheckinTime,
  );
  const filterLastCheckoutTimeParam = toApiDateParam(
    params?.q?.filterLastCheckoutTime,
  );
  const filterWorkerNameParam = params?.q?.workerNameCont?.trim();
  const filterNoteParam = params?.q?.noteCont?.trim();
  const filterCreatorNameParam = params?.q?.creatorNameCont?.trim();
  const filtersParams = buildMonthlyFiltersAxiosParams(params?.filters);

  const enabled =
    !!params &&
    !!companyId &&
    !!year &&
    !!month &&
    /^\d{4}$/.test(year) &&
    /^\d{1,2}$/.test(month) &&
    (isAdmin ||
      isWithoutWorker ||
      (typeof workerId === "number" &&
        Number.isFinite(workerId) &&
        workerId > 0));

  const qCacheKey = params?.q ? JSON.stringify(params.q) : "";
  const filtersCacheKey = params?.filters ? JSON.stringify(params.filters) : "";

  return useQuery(
    [
      QUERY_NAME.WORKER_LOGTIMES,
      companyId,
      "monthly",
      year,
      month,
      workerId ?? null,
      page,
      perPage,
      filterLogtimeStatuses ?? null,
      sortCacheKey || null,
      qCacheKey,
      filtersCacheKey,
    ],
    async () => {
      const monthPadded = month!.padStart(2, "0");
      const res = await axios({
        method: "get",
        url: SERVICES.WORKER_LOGTIMES + "/monthly",
        params: {
          company_id: companyId,
          year,
          month: monthPadded,
          page,
          per_page: perPage,
          ...(filterLogtimeStatuses
            ? { filter_logtime_statuses: filterLogtimeStatuses }
            : {}),
          ...(filterCreatedAtParam
            ? { filter_created_at: filterCreatedAtParam }
            : {}),
          ...(filterFirstCheckinTimeParam
            ? { filter_first_checkin_time: filterFirstCheckinTimeParam }
            : {}),
          ...(filterLastCheckoutTimeParam
            ? { filter_last_checkout_time: filterLastCheckoutTimeParam }
            : {}),
          ...(filterWorkerNameParam
            ? { filter_worker_names: filterWorkerNameParam }
            : {}),
          ...(filterNoteParam ? { filter_note: filterNoteParam } : {}),
          ...(filterCreatorNameParam
            ? { filter_creator_names: filterCreatorNameParam }
            : {}),
          ...sortParams,
          ...(!isWithoutWorker &&
          typeof workerId === "number" &&
          Number.isFinite(workerId) &&
          workerId > 0
            ? { worker_id: workerId }
            : {}),
          ...qParams,
          ...filtersParams,
        },
        paramsSerializer: {
          serialize: (p: Record<string, unknown>) => {
            const sp = new URLSearchParams();
            for (const [key, value] of Object.entries(p)) {
              if (Array.isArray(value)) {
                value.forEach((v) => sp.append(key, String(v)));
              } else if (value != null) {
                sp.append(key, String(value));
              }
            }
            return sp.toString();
          },
        },
      });
      const records = normalizeMonthlyPayload(res.data);
      const meta = normalizeMonthlyMeta(res.data, page, perPage);
      return { records, meta } as WorkerLogtimeMonthlyResult;
    },
    {
      cacheTime: 300,
      staleTime: 300,
      enabled,
      retry: workerLogtimeQueryRetry,
      onError(error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (!isWorkerLogtimeAccessDeniedStatus(status)) return;
        navigate(`/${ROUTES.NOT_FOUND_PAGE}`, { replace: true });
      },
    },
  );
};

export const useFetchWorkerLogtimesFilterOptions = (
  params: WorkerLogtimesFilterOptionsParams | null,
) => {
  const companyId = getCompanyId();
  const auth = useSelector(user);
  const isAdmin = !!auth?.is_admin;
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, companyId);
  const year = params?.year?.trim();
  const month = params?.month?.trim();
  const workerId = resolveMonthlyWorkerId(
    isAdmin,
    params?.workerId,
    currentWorkerId,
  );
  const refreshKey = params?.refreshKey ?? null;
  const activeFilters = params?.activeFilters;
  const activeFiltersCacheKey = activeFilters
    ? JSON.stringify(activeFilters)
    : null;
  const enabled =
    !!params &&
    !!companyId &&
    !!year &&
    !!month &&
    /^\d{4}$/.test(year) &&
    /^\d{1,2}$/.test(month) &&
    (isAdmin ||
      (typeof workerId === "number" &&
        Number.isFinite(workerId) &&
        workerId > 0));

  return useQuery(
    [
      QUERY_NAME.WORKER_LOGTIMES,
      companyId,
      "filter_options",
      year,
      month,
      workerId ?? null,
      refreshKey,
      activeFiltersCacheKey,
    ],
    async () => {
      const monthPadded = month!.padStart(2, "0");
      const activeFilterParams = Object.entries(activeFilters || {}).reduce(
        (acc, [key, values]) => {
          const cleaned = (values || [])
            .map((v) => String(v ?? "").trim())
            .filter(Boolean);
          if (cleaned.length) {
            acc[`active_filters[${key}][]`] = cleaned;
          }
          return acc;
        },
        {} as Record<string, string[]>,
      );
      const res = await axios({
        method: "get",
        url: SERVICES.WORKER_LOGTIMES + "/filter_options",
        params: {
          company_id: companyId,
          year,
          month: monthPadded,
          ...(typeof workerId === "number" &&
          Number.isFinite(workerId) &&
          workerId > 0
            ? { worker_id: workerId }
            : {}),
          ...activeFilterParams,
        },
        paramsSerializer: {
          serialize: (p: Record<string, unknown>) => {
            const sp = new URLSearchParams();
            for (const [key, value] of Object.entries(p)) {
              if (Array.isArray(value)) {
                value.forEach((v) => sp.append(key, String(v)));
              } else if (value != null) {
                sp.append(key, String(value));
              }
            }
            return sp.toString();
          },
        },
      });
      return (res.data || {}) as WorkerLogtimesFilterOptionsResponse;
    },
    {
      cacheTime: 300,
      staleTime: 300,
      enabled,
    },
  );
};

export type CheckCrossCompanyOverlapResponse = {
  has_overlap: boolean;
  overlapping_company_name?: string;
  msg?: string;
};

export const checkCrossCompanyOverlap = async (params: {
  company_id: string;
  worker_id: number;
  date: string;
  checkin_logged_at: string;
  checkout_logged_at?: string;
}): Promise<CheckCrossCompanyOverlapResponse> => {
  const res = await axios({
    method: "get",
    url: `${SERVICES.WORKER_LOGTIMES}/check_cross_company_overlap`,
    params,
  });
  return res.data as CheckCrossCompanyOverlapResponse;
};

export type WorkerLogtimesSaveJsonItem = {
  id?: number;
  log_type?: string;
  logged_at?: string;
  note?: string | null;
  _destroy?: boolean;
};

export type WorkerLogtimesSaveJsonPayload = {
  worker_id: number;
  company_id: string;
  date: string;
  logtimes: WorkerLogtimesSaveJsonItem[];
};

export type WorkerLogtimesSaveBody = FormData | WorkerLogtimesSaveJsonPayload;

export const saveWorkerLogtimes = (body: WorkerLogtimesSaveBody) => {
  if (body instanceof FormData) {
    return axios.post(`${SERVICES.WORKER_LOGTIMES}/save`, body);
  }
  return axios.post(`${SERVICES.WORKER_LOGTIMES}/save`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const useSaveWorkerLogtimesSave = () => useMutation(saveWorkerLogtimes);

export enum GeolocationErrorCode {
  NOT_SUPPORTED = "NOT_SUPPORTED",
  PERMISSION_DENIED = "PERMISSION_DENIED",
  POSITION_UNAVAILABLE = "POSITION_UNAVAILABLE",
  TIMEOUT = "TIMEOUT",
  UNKNOWN = "UNKNOWN",
}
type GPSPosition = {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  errorCode: GeolocationErrorCode | null;
};

export function useGeolocation(): GPSPosition {
  const [position, setPosition] = useState<GPSPosition>({
    latitude: null,
    longitude: null,
    accuracy: null,
    errorCode: null,
  });

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setPosition((prev) => ({
        ...prev,
        errorCode: GeolocationErrorCode.NOT_SUPPORTED,
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          errorCode: null,
        });
      },
      (err) => {
        let errorCode = GeolocationErrorCode.UNKNOWN;

        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorCode = GeolocationErrorCode.PERMISSION_DENIED;
            break;
          case err.POSITION_UNAVAILABLE:
            errorCode = GeolocationErrorCode.POSITION_UNAVAILABLE;
            break;
          case err.TIMEOUT:
            errorCode = GeolocationErrorCode.TIMEOUT;
            break;
        }

        setPosition((prev) => ({
          ...prev,
          errorCode,
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }, []);

  return position;
}
